import { z } from 'zod';
import { APP_PLATFORM_CONSTRAINTS, type AppPlatformPackageKind } from '@/lib/constants/appPlatform';
import { AppPlatformUtils } from '../AppPlatformUtils';

const title = z
  .string()
  .trim()
  .min(1, 'App name is required')
  .max(APP_PLATFORM_CONSTRAINTS.TITLE_MAX);

/**
 * Settings form of one generic platform row. The two store fields render — and
 * are validated — for operators only: for everyone else they hold the stored
 * values untouched and never reach the request
 * (AppPlatformUtils.transformFormToUpdateReq), so a rule on a field the person
 * cannot see must never block their save.
 */
export function appPlatformFormSchema(packageKind: AppPlatformPackageKind, isOperator: boolean) {
  const base = z.object({
    title,
    appStateTypeId: z.number(),
    storePackageId: z.string(),
    storeAppId: z.string(),
  });
  if (!isOperator) return base;
  return base
    .refine((v) => AppPlatformUtils.isValidStorePackageId(packageKind, v.storePackageId), {
      path: ['storePackageId'],
      message:
        packageKind === 'android'
          ? 'Enter an Android package name, like com.example.tv (letters, digits and underscores, dot-separated)'
          : 'Enter a bundle id, like com.example.tv',
    })
    .refine((v) => AppPlatformUtils.isValidStoreAppId(v.storeAppId), {
      path: ['storeAppId'],
      message: 'Enter the id without spaces',
    })
    .refine((v) => AppPlatformUtils.appIdHasPackage(v), {
      path: ['storeAppId'],
      message: 'Set the package name before the store app id',
    });
}

export type AppPlatformFormSchemaData = z.infer<ReturnType<typeof appPlatformFormSchema>>;

export const appPlatformEnableFormSchema = z.object({ title });

export type AppPlatformEnableFormSchemaData = z.infer<typeof appPlatformEnableFormSchema>;
